/**
 * Echo Hunt -- the weaver (P8, §8.1 + §8.2).
 *
 * One call per round: it takes the bullet pool (already shuffled, labels not present at all -- the
 * only thing that exists here is `{slotId, text}`, §16.0) plus the world, the character and the
 * storyline summary, and returns the bullets in a story order with bridged text.
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Weaver" -- blind to provenance, sees world/character/summary and all
 * the bullets. `Prompts/Weave.ts` documents how that is enforced structurally.
 *
 * ## The locked-ID contract (§8.1 `[LIKELY]`)
 *
 * The weaver returns `{order: id[], bullets: {id, text}[]}`. Output bullet *i* corresponds to input
 * bullet *i* because both are addressed BY ID, so provenance stays exact and no backtracking or slot
 * re-matching is needed. `ValidateWeave` enforces:
 *
 *  - the same id SET in, out and in `order` (no additions, drops, duplicates);
 *  - the same id COUNT (so "one merged into another" and "one split into two" both fail);
 *  - non-empty text per bullet;
 *  - every woven bullet still passes the §7 gate (`CheckBullet`), because a bridging sentence is NEW
 *    text and can reintroduce a `--` dash or a banned term (§7, §17);
 *  - a `summaryOut` that is non-empty.
 *
 * There is NO retention check on woven text. Retention (§6) exists to stop the RESHAPE from replacing
 * a real bullet's event; the weaver is explicitly told to keep every event, and a rule that re-ran an
 * LLM check per bullet would cost one call per bullet for a property `ValidateWeave` can only partly
 * see anyway. Instead the erosion risk is measured and LOGGED (`wovenEditDistance`, the §13.3
 * bridging-spread diagnostic): a round where the weaver rewrote everything heavily shows up as a
 * number, and P10's rewritten-vs-untouched hit-rate diagnostic reads it.
 *
 * ## Failure handling
 *
 * On a validation failure the weaver is re-asked ONCE with the specific problems in plain language
 * (§8.1 binding "On failure, retry the weaver once with the validation error, then fail the round"). A
 * second failure FAILS THE ROUND -- it does not degrade into a partial weave. A round with bullets
 * that never joined a story is not a round: the user would be hunting echoes in prose the weaver never
 * actually produced, and the §13.1 round log would record a `wovenText` that is really just
 * `reshapedText` under a different name.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The LLM is passed in as an
 * `LLMBackend`, so tests use `MockLLMBackend`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.1, §8.2, §2 (#2, #4, #8), §16.2 P8.
 */
import {CallLLM, LLMBackend, LLMCallCounter, PreviewText} from "./LLM.js";
import {CheckBullet, CheckBulletOptions} from "./Normalize.js";
import type {DoorDecision, EchoCharacter, EchoDoorOffer, EchoWorld, RoundSlot} from "./Types.js";
import {FRAME_DREAMING_BUT_AWAKE} from "./Worlds.js";
import {
	Weave_JSONSchema,
	Weave_SystemPrompt,
	Weave_UserPrompt,
	Weave_UserPromptOptions,
} from "./Prompts/Weave.js";


// input / output shapes
// ==========

/** One bullet as the weaver may see it: an opaque id and its text. There is deliberately NO field for
 * provenance -- a caller cannot pass `sourceKind` even by mistake (§16.0 label hygiene). */
export type WeaveBullet = {
	slotId: string;
	text: string;
};

/** The weaving inputs that come from the profile/round rather than from the bullets. */
export type WeaveContext = {
	world: EchoWorld;
	character: EchoCharacter;
	/** `FRAME_DREAMING_BUT_AWAKE` in v1 (§8.2) */
	frame?: string|n;
	/** the storyline coming into this round (§8.1); null on a story's first installment */
	summary?: string|n;
	/** user-set in-dream schematic rules (§8.1) */
	schematicRules?: string[]|n;
	/** P11/§11.5: the user pressed the rescue/boredom button, so this round must present a way out whether or
	 * not the material suggests one. Deliberately a HINT, not an instruction to teleport the character: the
	 * door still opens in the world's own terms and the character still decides whether to use it. */
	requestDoor?: boolean|n;
	/** §11.5 W3: the world a door this round offers would lead to. Fixed BEFORE the door is described, so the
	 * far side can be written honestly and resolution is a lookup. Null = nowhere else to go, so no offer. */
	doorTarget?: {id: string, name: string, bible: string}|n;
	/** §11.5 W2: a door the player accepted on a previous round, now a thing in the world. When set, the
	 * round asks the CHARACTER to decide, and forbids emitting a new offer. */
	stagedDoor?: {description: string, targetWorldId?: string|n}|n;
	/** §11.5 W4: the arrival, when the story moved since the previous installment. */
	worldChange?: {
		fromWorldName?: string|n;
		cause?: string|n;
		doorDescription?: string|n;
	}|n;
};

/** What the weaver replied, after parsing and normalisation. */
export type WeaveOutput = {
	/** the bullet ids in story order, as the weaver returned them */
	order: string[];
	/** woven text per id */
	bullets: {id: string, text: string}[];
	doorOffer: EchoDoorOffer|null;
	/** §11.5 W2: the CHARACTER's in-fiction decision, present only when a staged door was offered to them.
	 * Null when there was no door to decide about -- a decision is meaningful only if one existed. */
	doorDecision: DoorDecision|null;
	summaryOut: string;
};

/** The problems `ValidateWeave` found. Empty = the weave is usable. */
export type WeaveValidation = {
	ok: boolean;
	problems: string[];
};

/** The result of one weaving attempt (`WeaveStory`). */
export type WeaveResult = {
	/** null when the weave failed after the retry -- the round must not be played (§8.1) */
	output: WeaveOutput|null;
	validation: WeaveValidation;
	/** every reply received, newest last, for the round log and the dev tool */
	attempts: {reply: string, problems: string[]}[];
	outputTokens: number;
	/** the number of weaver calls made (1 = no retry) */
	callCount: number;
};

export type WeaveOptions = {
	context: WeaveContext;
	bullets: WeaveBullet[];
	/** the `weaver` role's model; "" lets the backend decide */
	model?: string|n;
	/** §7 gate options (banned terms + cliché blacklist) applied to every WOVEN bullet */
	checkOptions?: CheckBulletOptions|n;
	/** tell the weaver it may reword freely (Settings -> Weaver, off by default; 2026-10-21) */
	includeParaphraseNote?: boolean|n;
	/** per-run tally (§13.1) */
	counter?: LLMCallCounter|n;
	/** total weaver calls before failing the round. Default 2 (§8.1: retry once, then fail). */
	maxAttempts?: number;
};

// reply parsing
// ==========

export type ParsedWeaveReply = {
	output: WeaveOutput|null;
	/** problems found while parsing (distinct from `ValidateWeave`'s semantic checks, but reported
	 * through the same channel so the model gets one coherent list) */
	problems: string[];
};

/** Coerces a raw parsed reply into a `WeaveOutput`, salvaging what is coercible and reporting what is
 * not. Same posture as the index pass (§16.2 P5): a null `doorOffer` and a missing `order` are
 * recoverable (the order can be taken from `bullets`), while a missing bullet entry is not. */
export function ParseWeaveReply(parsed: unknown): ParsedWeaveReply {
	const problems: string[] = [];

	if (parsed == null || typeof parsed != "object" || Array.isArray(parsed)) {
		return {output: null, problems: ["The reply was not a JSON object."]};
	}
	const obj = parsed as Record<string, unknown>;

	// bullets: the load-bearing part, and the only thing that must be complete
	const rawBullets = Array.isArray(obj.bullets) ? obj.bullets : null;
	if (rawBullets == null) {
		return {output: null, problems: ["The reply has no `bullets` array."]};
	}

	const bullets: {id: string, text: string}[] = [];
	for (const [index, rawBullet] of rawBullets.entries()) {
		if (rawBullet == null || typeof rawBullet != "object") {
			problems.push(`bullets[${index}] is not an object.`);
			continue;
		}
		const entry = rawBullet as Record<string, unknown>;
		const id = typeof entry.id == "string" ? entry.id.trim() : "";
		const text = typeof entry.text == "string" ? entry.text.trim() : "";
		if (id.length == 0) {
			problems.push(`bullets[${index}] has no \`id\`.`);
			continue;
		}
		if (text.length == 0) {
			problems.push(`bullets[${index}] (id ${id}) has no \`text\`.`);
			continue;
		}
		bullets.push({id, text});
	}

	// order: recoverable, since the bullets array is itself in some order. Reporting it still matters:
	// a reply that omits `order` is a reply that did not follow the contract, which is worth saying.
	let order: string[] = [];
	if (Array.isArray(obj.order)) {
		order = obj.order.map(a=>typeof a == "string" ? a.trim() : "").filter(a=>a.length > 0);
	} else {
		problems.push("The reply has no `order` array; the order of `bullets` was used instead.");
		order = bullets.map(a=>a.id);
	}

	// doorOffer: either an object with a description, or null/absent (§8.3)
	let doorOffer: EchoDoorOffer|null = null;
	const rawDoor = obj.doorOffer;
	if (rawDoor != null && typeof rawDoor == "object" && !Array.isArray(rawDoor)) {
		const description = (rawDoor as Record<string, unknown>).description;
		if (typeof description == "string" && description.trim().length > 0) {
			doorOffer = {description: description.trim()};
		} else {
			problems.push("`doorOffer` has no non-empty `description`; it was treated as absent.");
		}
	} else if (typeof rawDoor == "string" && rawDoor.trim().length > 0) {
		// a bare string is a common near-miss, and salvaging it changes nothing the user can observe
		doorOffer = {description: rawDoor.trim()};
	}

	const summaryOut = typeof obj.summaryOut == "string" ? obj.summaryOut.trim() : "";
	if (summaryOut.length == 0) {
		problems.push("`summaryOut` is missing or empty.");
	}

	// doorDecision: the CHARACTER's decision about a door already in the world (§11.5 W2). Absent/null is
	// normal -- every round where no door was staged has nothing to decide. An unrecognised value is
	// reported and treated as absent rather than guessed, because guessing would move the story.
	let doorDecision: DoorDecision|null = null;
	const rawDecision = obj.doorDecision;
	if (typeof rawDecision == "string") {
		const normalized = rawDecision.trim().toLowerCase();
		if (normalized == "taken" || normalized == "declined") {
			doorDecision = normalized as DoorDecision;
		} else if (normalized.length > 0 && normalized != "null") {
			problems.push(`\`doorDecision\` was "${normalized}"; expected "taken", "declined" or null. It was treated as absent.`);
		}
	}

	return {output: {order, bullets, doorOffer, doorDecision, summaryOut}, problems};
}

// validation
// ==========

/** Checks one weave against the input it was produced from (§8.1 "same ID set, no merges/splits/
 * additions/drops, every text non-empty, each woven bullet still passes `CheckBullet`").
 *
 * `null`/`undefined` output (a reply that could not be parsed at all) is a failure with one problem,
 * so the caller has a single shape to log.
 */
export function ValidateWeave(inputBullets: WeaveBullet[], output: WeaveOutput|null, checkOptions?: CheckBulletOptions|n, doorOptions?: {stagedDoor?: boolean|n}|n): WeaveValidation {
	if (output == null) {
		return {ok: false, problems: ["No usable reply."]};
	}

	const problems: string[] = [];
	const expectedIds = new Set(inputBullets.map(a=>a.slotId));
	const returnedIds = output.bullets.map(a=>a.id);
	const returnedSet = new Set(returnedIds);

	// 1) the SET must match: no additions, no drops
	const missing = [...expectedIds].filter(id=>!returnedSet.has(id));
	const unexpected = [...returnedSet].filter(id=>!expectedIds.has(id));
	if (missing.length > 0) {
		problems.push(`Missing ${missing.length} bullet(s): ${missing.join(", ")}. Every bullet must be returned.`);
	}
	if (unexpected.length > 0) {
		problems.push(`${unexpected.length} bullet id(s) were not in the input: ${unexpected.join(", ")}. Do not invent bullets.`);
	}

	// 2) the COUNT must match, which catches a merge (fewer entries, ids still all present) and a split
	//    (more entries under one id) that the set comparison above cannot see
	if (output.bullets.length != inputBullets.length) {
		problems.push(`Returned ${output.bullets.length} bullet(s) for ${inputBullets.length} input bullet(s). Never merge, split, add or drop bullets.`);
	}

	// 3) no duplicate ids: two entries for one id means one of them has no provenance
	const duplicates = FindDuplicates(returnedIds);
	if (duplicates.length > 0) {
		problems.push(`Bullet id(s) returned more than once: ${duplicates.join(", ")}.`);
	}

	// 4) `order` must be exactly the returned id set, once each
	const orderDuplicates = FindDuplicates(output.order);
	if (orderDuplicates.length > 0) {
		problems.push(`\`order\` lists the same id more than once: ${orderDuplicates.join(", ")}.`);
	}
	const orderMissing = [...returnedSet].filter(id=>!output.order.includes(id));
	const orderUnexpected = output.order.filter(id=>!returnedSet.has(id));
	if (orderMissing.length > 0) {
		problems.push(`\`order\` is missing ${orderMissing.length} bullet(s): ${orderMissing.join(", ")}.`);
	}
	if (orderUnexpected.length > 0) {
		problems.push(`\`order\` contains ${orderUnexpected.length} id(s) that are not in \`bullets\`: ${orderUnexpected.join(", ")}.`);
	}

	// 5) non-empty text (the parse step already rejects empty text, but a weave can be built by a caller
	//    too, and this is the cheap place to be sure)
	for (const bullet of output.bullets) {
		if (bullet.text.trim().length == 0) {
			problems.push(`Bullet ${bullet.id} has empty text.`);
		}
	}

	// 6) the §7 gate on the WOVEN text: bridging is new prose and can reintroduce a `--` dash or a
	//    banned term even when every source bullet was clean
	for (const bullet of output.bullets) {
		const violations = CheckBullet(bullet.text, checkOptions ?? {});
		if (violations.length > 0) {
			problems.push(`Bullet ${bullet.id} breaks the house style after weaving: ${violations.map(a=>a.message).join(" ")}`);
		}
	}

	// 7) the summary is the next round's input, so an empty one would silently restart the story
	if (output.summaryOut.trim().length == 0) {
		problems.push("`summaryOut` is empty. Write the 3-6 sentence record of what happened.");
	}

	// 8) §11.5 W2: a round that PRESENTS a staged door must report the character's decision, or the story
	//    cannot know whether to move. A missing decision is not defaultable: `declined` would quietly close a
	//    door the character walked through, and `taken` would move a story they stayed in.
	if (doorOptions?.stagedDoor == true) {
		if (output.doorDecision == null) {
			problems.push("`doorDecision` is missing. A way out was present this round, so say whether the character used it: `taken` or `declined`.");
		}
		if (output.doorOffer != null) {
			problems.push("A way out was already present this round, so `doorOffer` must be null -- two doors in one installment is ambiguous about which one was decided.");
		}
	}

	return {ok: problems.length == 0, problems};
}

// the weave
// ==========

/** Weaves one round's bullets into a story installment (§8.1), with ONE informed re-ask on a validation
 * failure and then a hard failure. Never degrades to a partial weave -- see the module comment. */
export async function WeaveStory(backend: LLMBackend, options: WeaveOptions): Promise<WeaveResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 2);
	const attempts: {reply: string, problems: string[]}[] = [];
	let outputTokens = 0;
	let callCount = 0;
	let fixNotes: string|n = null;

	let lastOutput: WeaveOutput|null = null;
	let lastValidation: WeaveValidation = {ok: false, problems: ["No attempt was made."]};

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const promptOptions: Weave_UserPromptOptions = {
			frame: options.context.frame ?? FRAME_DREAMING_BUT_AWAKE,
			worldName: options.context.world.name,
			worldBible: options.context.world.bible,
			worldCrazinessTolerance: options.context.world.crazinessTolerance,
			characterName: options.context.character.name,
			characterCautionVsOpportunity: options.context.character.values_cautionVsOpportunity,
			characterNotes: options.context.character.personalityNotes,
			summary: options.context.summary,
			schematicRules: options.context.schematicRules,
			// P11: the override asks for a door, it does not order the character through one (§8.3)
			requestDoor: options.context.requestDoor == true,
			// §11.5 W3: the target is fixed BEFORE the description, so the far side can be named honestly
			doorTargetName: options.context.doorTarget?.name ?? null,
			doorTargetBible: options.context.doorTarget?.bible ?? null,
			// §11.5 W2: a staged door turns this round into the CHARACTER's decision instead of a new offer
			stagedDoorDescription: options.context.stagedDoor?.description ?? null,
			// §11.5 W4: emitted for exactly one round after a move
			worldChange: options.context.worldChange ?? null,
			// `{id, text}` pairs only: the weaver has no way to see provenance (§16.0)
			bullets: options.bullets.map(bullet=>({id: bullet.slotId, text: bullet.text})),
			includeParaphraseNote: options.includeParaphraseNote == true,
			fixNotes,
		};

		const result = await CallLLM(backend, {
			role: "weaver",
			model: options.model ?? null,
			system: Weave_SystemPrompt(),
			user: Weave_UserPrompt(promptOptions),
			json: Weave_JSONSchema(),
			counter: options.counter ?? null,
		});
		callCount++;
		outputTokens += result.outputTokens;

		if (result.error != null) {
			// CallLLM already retried the transport/parse layer; this is the outer, semantic attempt
			attempts.push({reply: result.text, problems: [result.error]});
			lastValidation = {ok: false, problems: [result.error]};
			fixNotes = result.error;
			continue;
		}

		const parsed = ParseWeaveReply(result.parsed);
		const validation = ValidateWeave(options.bullets, parsed.output, options.checkOptions, {
			stagedDoor: options.context.stagedDoor != null,
		});

		// a parse problem and a semantic problem are reported together, so the re-ask gets one list
		const problems = [...parsed.problems, ...validation.problems];
		attempts.push({reply: result.text, problems});
		lastOutput = parsed.output;
		lastValidation = {ok: problems.length == 0, problems};

		if (lastValidation.ok) break;
		fixNotes = problems.join("\n");
	}

	if (!lastValidation.ok) {
		// the round cannot be played (§8.1): a half-woven story would put `reshapedText` in front of the
		// user as if the weaver had produced it
		return {output: null, validation: lastValidation, attempts, outputTokens, callCount};
	}

	return {output: lastOutput, validation: lastValidation, attempts, outputTokens, callCount};
}

// applying a weave to the slots
// ==========

/** Fills each slot's `wovenText`/`finalText` from a validated weave, in STORY order, and records the
 * `wovenEditDistance` bridging-spread diagnostic (§8.1, §13.3).
 *
 * The slots are returned in story order (the weaver's `order`), not in the input order: the hunt shows
 * the story as written, and the shuffled input order carries no meaning of its own. Every label field
 * is passed through untouched -- this function never looks at `sourceKind`.
 *
 * Throws if the weave is invalid, because applying a weave that failed validation would mean shipping
 * bullets the validator is supposed to stop.
 */
export function ApplyWeave(slots: RoundSlot[], output: WeaveOutput): RoundSlot[] {
	const byId = new Map(slots.map(slot=>[slot.slotId, slot]));
	const textById = new Map(output.bullets.map(bullet=>[bullet.id, bullet.text]));

	const applied: RoundSlot[] = [];
	for (const slotId of output.order) {
		const slot = byId.get(slotId);
		const text = textById.get(slotId);
		if (slot == null || text == null) {
			throw new Error(`ApplyWeave: the weave references slot "${slotId}", which is not in the pool. Validate the weave first.`);
		}
		applied.push({
			...slot,
			wovenText: text,
			finalText: text, // until a repair rewrite replaces it (§9.3)
			wovenEditDistance: EditDistanceWords(slot.reshapedText, text),
		});
	}

	if (applied.length != slots.length) {
		throw new Error(`ApplyWeave: ${applied.length} of ${slots.length} slots were applied. Validate the weave first.`);
	}
	return applied;
}

/** The label-free view model the hunt UI may receive once the weave is applied (§16.0). Deliberately the
 * same `{slotId, text}` shape P7 established (`HuntSlotView`), so the UI consumes one type before the
 * weave and after it, and the pre-reveal component cannot be handed a label by mistake. */
export function ToWovenHuntSlotView(slots: RoundSlot[]|n): {slotId: string, text: string}[] {
	return (slots ?? []).map(slot=>({slotId: slot.slotId, text: slot.finalText.length > 0 ? slot.finalText : slot.wovenText}));
}

// the bridging-spread diagnostic (§8.1, §13.3)
// ==========

/** Word-level Levenshtein distance between two texts, case- and punctuation-insensitive.
 *
 * This is the §8.1 "bridging-spread heuristic (log only)": it measures how much the weaver rewrote a
 * bullet. The plan asks for it per bullet so §13.3's "rewritten vs. untouched hit rate" diagnostic can
 * exist -- if the user finds echoes far more often in lightly-rewritten bullets, weaving has become a
 * tell and needs to be gentler.
 *
 * LOG ONLY: nothing branches on it. Using it to gate the weave would reward a weaver that changes
 * nothing, which is the opposite of the job. */
export function EditDistanceWords(a: string, b: string): number {
	// previous row in `a`'s iteration order, so row 1 of `a` is not lost
	const left = a.split(/\s+/).filter(a=>a.length > 0).map(NormalizeWord);
	const right = b.split(/\s+/).filter(a=>a.length > 0).map(NormalizeWord);

	if (left.length == 0) return right.length;
	if (right.length == 0) return left.length;

	let previous = [...Array(right.length + 1).keys()];
	for (let i = 1; i <= left.length; i++) {
		const current = [i];
		for (let j = 1; j <= right.length; j++) {
			const substitution = previous[j - 1] + (left[i - 1] == right[j - 1] ? 0 : 1);
			current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, substitution);
		}
		previous = current;
	}
	return previous[right.length];
}

function NormalizeWord(word: string): string {
	return word.toLowerCase().replace(/^[^a-z0-9']+|[^a-z0-9']+$/g, "");
}

/** The values that appear more than once, in first-seen order. Used for both the returned bullet ids and
 * the `order` list -- a duplicate in either is a provenance bug, not a style choice. */
export function FindDuplicates(values: string[]): string[] {
	const seen = new Set<string>();
	const duplicates: string[] = [];
	for (const value of values) {
		if (seen.has(value)) {
			if (!duplicates.includes(value)) duplicates.push(value);
		} else {
			seen.add(value);
		}
	}
	return duplicates;
}

/** The mean `wovenEditDistance` as a fraction of the original word count, and the per-slot spread.
 * Shown in the dev tool and logged with the round: a story where the weaver rewrote everything heavily
 * ("spread" clustering near 1) is the case §13.3 warns about. */
export type BridgingSpread = {
	meanRewriteFraction: number;
	maxRewriteFraction: number;
	/** how many slots were left essentially untouched (< 10% of their words changed) */
	untouchedCount: number;
	/** how many slots were heavily rewritten (> 50% of their words changed) */
	heavyCount: number;
};

export function ComputeBridgingSpread(slots: RoundSlot[]|n): BridgingSpread {
	const usable = (slots ?? []).filter(slot=>slot.wovenEditDistance != null);
	if (usable.length == 0) return {meanRewriteFraction: 0, maxRewriteFraction: 0, untouchedCount: 0, heavyCount: 0};

	let sum = 0;
	let max = 0;
	let untouched = 0;
	let heavy = 0;
	for (const slot of usable) {
		const words = Math.max(1, slot.reshapedText.split(/\s+/).filter(a=>a.length > 0).length);
		const fraction = Math.min(1, (slot.wovenEditDistance ?? 0) / words);
		sum += fraction;
		max = Math.max(max, fraction);
		if (fraction < .1) untouched++;
		if (fraction > .5) heavy++;
	}

	return {
		meanRewriteFraction: sum / usable.length,
		maxRewriteFraction: max,
		untouchedCount: untouched,
		heavyCount: heavy,
	};
}

// dev-tool helpers
// ==========

/** Renders a failed weave's problems as one loggable block. Exported so the Settings dev tool and the
 * round record describe a failure the same way. */
export function DescribeWeaveFailure(validation: WeaveValidation, attempts: {reply: string}[]): string {
	const lastReply = attempts[attempts.length - 1]?.reply ?? "(no reply)";
	return [
		`The weave failed after ${attempts.length} attempt(s), so the round cannot be played.`,
		...validation.problems.map(problem=>`- ${problem}`),
		"",
		`Last reply: ${PreviewText(lastReply, 400)}`,
	].join("\n");
}
